import { useState, useEffect } from "react";

import { useCreateProject, useEditProject } from "../mutations";
import { validationRules } from "../../../shared/utils/validationRules";
import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";
import { useProject } from "../../projectOverview/hooks/useProject";
import { Asset, ProjectFormData } from "../types";
import { useFetchProjects } from "./useFetchProjects";
import { useFetchPortfolios } from "../../portfolio/hooks/useFetchPortfolios";
import { useDropdown } from "../../../hooks/useDropdown";
import { useThirdPartySites } from "./useThirdPartySites";
import dayjs from "dayjs";
import { formatApiEndDate, formatApiStartDate } from "../../../shared/utils";

export const useAddProject = ({ isEdit, projectId }: { isEdit: boolean, projectId?: string }) => {

    const { getOptions } = useDropdown();

    const createProject = useCreateProject();
    const editProject = useEditProject();

    const { isPending: isProjectDetailsPending, projectData, removeCache: removeProjectCache } = useProject(projectId);
    const { removeCache: removePortfolioCache } = useFetchPortfolios(false);

    const { removeCachePinnedProjects: removeCachePinnedTrue, removeCacheUnpinnedProjects: removeCacheUnpinnedTrue, removeCachePinnedStages: removeCachePinnedStagesTrue, removeCacheUnpinnedStages: removeCacheUnpinnedStagesTrue } = useFetchProjects(true, false, undefined, { my_tasks: false });
    const { removeCachePinnedProjects: removeCachePinnedFalse, removeCacheUnpinnedProjects: removeCacheUnpinnedFalse, removeCachePinnedStages: removeCachePinnedStagesFalse, removeCacheUnpinnedStages: removeCacheUnpinnedStagesFalse } = useFetchProjects(false, false, undefined, { my_tasks: false });
    const { removeCachePinnedProjects: removeCachePinnedTrueCompleted, removeCacheUnpinnedProjects: removeCacheUnpinnedTrueCompleted, removeCachePinnedStages: removeCachePinnedStagesTrueCompleted, removeCacheUnpinnedStages: removeCacheUnpinnedStagesTrueCompleted } = useFetchProjects(false, true, undefined, { my_tasks: false });

    const removeCache = () => {
        removeProjectCache();
        removePortfolioCache();
        removeCachePinnedTrue();
        removeCachePinnedFalse();
        removeCacheUnpinnedTrue();
        removeCacheUnpinnedFalse();
        removeCachePinnedStagesTrue();
        removeCachePinnedStagesFalse();
        removeCacheUnpinnedStagesTrue();
        removeCacheUnpinnedStagesFalse();
        removeCachePinnedTrueCompleted();
        removeCacheUnpinnedTrueCompleted();
        removeCachePinnedStagesTrueCompleted();
        removeCacheUnpinnedStagesTrueCompleted();
    };

    const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
    const [otherSite, setOtherSite] = useState<boolean>(false);

    const { data: thirdPartySites, refetch: refetchThirdPartySites } = useThirdPartySites();

    const [formData, setFormData] = useState<ProjectFormData>({
        title: null,
        description: null,
        portfolio: null,
        programme: null,
        workstream: null,
        project_year: null,
        project_scope: null,
        wbs_code_id: null,
        project_budget: null,
        project_income: null,
        site_name: null,
        site_description: null,
        org: 1,
        region: null,
        g_17: null,
        project_status: null,
        address: null,
        what3_words: null,
        planned_start_date: null,
        planned_commissioning_date: null,
        planned_close_up_date: null,
        lat: null,
        long: null,
        work_type: null,
        work_type_group: null,
    });

    const [assetErrors, setAssetErrors] = useState<Record<number, Record<string, string | null>>>({});
    const [assets, setAssets] = useState<Asset[]>([
        {
            id: 1,
            asset_programme: null,
            narms_value: "",
            asset_category: null,
            asset_description: "",
            asset_type: null,
            iln_number_id: "",
            functional_location: "",
            sap_hierarchy: "",
            asset_budget: "",
            asset_id: undefined,
        }
    ]);

    const [stakeholders, setStakeholders] = useState<{ label: string, value: string }[]>([]);
    const [openStakeholderDialog, setOpenStakeholderDialog] = useState(false);

    const validationConfig: Record<string, string[]> = {
        title: ["is_empty", "only_string"],
        description: ["is_empty"],
        portfolio: ["is_empty"],
        programme: ["is_empty"],
        workstream: ["is_empty"],
        project_year: ["is_empty"],
        // project_scope: ["is_empty"],
        // wbs_code_id: ["is_empty"],
        project_budget: ["is_empty", "decimal_number"],
        project_income: ["decimal_number"],
        site_name: ["is_empty"],
        // address: ["is_empty"],
        org: ["is_empty"],
        region: ["is_empty"],
        // county: ["is_empty"],
        // coamArea: ["is_empty"],
        planned_start_date: ["is_empty"],
        // planned_end_date: ["is_empty"],
        planned_commissioning_date: ["is_empty"],
        planned_close_up_date: ["is_empty"],
        // phase_template: ["is_empty"],
        work_type: ["is_empty"],
        work_type_group: ["is_empty"],
    };

    const assetValidationConfig: Record<string, string[]> = {
        asset_programme: ["is_empty"],
        narms_value: ["is_empty"],
        asset_category: ["is_empty"],
        asset_type: ["is_empty"],
        asset_description: ["is_empty"],
        iln_number_id: ["is_empty"],
        functional_location: ["is_empty"],
        sap_hierarchy: ["is_empty"],
        asset_budget: ["is_empty", "decimal_number"],
    };

    const validateField = (key: string, value: any): string | null => {
        const rules = validationConfig[key];
        if (!rules) return null;
        for (const ruleName of rules) {
            const rule = validationRules[ruleName];
            if (rule) {
                const error = rule(value);
                if (error) return error;
            }
        }
        return null;
    };

    const validateAllFields = (): { isAllFieldValid: boolean, errors: any } => {
        let newErrors: Record<string, string | null> = {};
        Object.keys(validationConfig).forEach((key) => {
            const error = validateField(key, (formData as any)[key]);
            if (error) newErrors[key] = error;
        });
        setFieldErrors(newErrors);
        const isAllFieldValid = Object.values(newErrors).every((error) => !error);
        return { isAllFieldValid, errors: newErrors };
    };

    const validateAssetField = (key: string, value: any): string | null => {
        const rules = assetValidationConfig[key];
        if (!rules) return null;
        for (const ruleName of rules) {
            const rule = validationRules[ruleName];
            if (rule) {
                const error = rule(value);
                if (error) return error;
            }
        }
        return null;
    };

    const validateAssets = (): boolean => {
        let newAssetErrors: Record<number, Record<string, string | null>> = {};

        // Track asset descriptions for duplicate checking
        const assetDescriptions = new Map<string, number[]>();

        assets.forEach((asset) => {
            let assetFieldErrors: Record<string, string | null> = {};
            Object.keys(assetValidationConfig).forEach((key) => {
                if (key === "narms_value" && asset.asset_programme && asset.asset_programme !== 6) {
                    return;
                }
                const error = validateAssetField(key, (asset as any)[key]);
                if (error) assetFieldErrors[key] = error;
            });

            // Track asset descriptions for duplicate detection
            if (asset.asset_description && asset.asset_description.trim()) {
                const normalizedDesc = asset.asset_description.trim().toLowerCase();
                if (!assetDescriptions.has(normalizedDesc)) {
                    assetDescriptions.set(normalizedDesc, []);
                }
                assetDescriptions.get(normalizedDesc)!.push(asset.id);
            }

            if (Object.values(assetFieldErrors).some((error) => error)) {
                newAssetErrors[asset.id] = assetFieldErrors;
            }
        });

        // Check for duplicate asset descriptions and mark them
        assetDescriptions.forEach((assetIds, description) => {
            if (assetIds.length > 1) {
                // Multiple assets have the same description
                assetIds.forEach((assetId) => {
                    if (!newAssetErrors[assetId]) {
                        newAssetErrors[assetId] = {};
                    }
                    newAssetErrors[assetId].asset_description = "This asset description is already used. Please use a unique description.";
                });
            }
        });

        setAssetErrors(newAssetErrors);
        return Object.keys(newAssetErrors).length === 0;
    };

    const handleFieldChange = (key: string, value: any) => {
        setFormData((prev) => {
            const newData = { ...prev, [key]: value };
            if (key === "planned_start_date") {
                newData.planned_commissioning_date = null;
                newData.planned_close_up_date = null;
            } else if (key === "planned_commissioning_date") {
                newData.planned_close_up_date = null;
            }
            return newData;
        });
        const error = validateField(key, value);
        setFieldErrors((prev) => {
            const newErrors = { ...prev, [key]: error };
            if (key === "planned_start_date") {
                newErrors.planned_commissioning_date = null;
                newErrors.planned_close_up_date = null;
            } else if (key === "planned_commissioning_date") {
                newErrors.planned_close_up_date = null;
            }
            return newErrors;
        });
    };

    const getSiteOptions = () => {
        if (formData?.region) {
            const raw = getOptions('site');
            const options = Array.isArray(raw)
                ? raw.sort((a, b) => a.description.localeCompare(b.description))
                : [];
            return options.filter((option: AutocompleteOption) => option.network_id === formData.region);
        }
        return [];
    };

    const handleOtherSiteChange = () => {
        setOtherSite((prev) => !prev);
        setFormData((prev) => ({ ...prev, site_name: null, address: null, what3_words: null, lat: null, long: null }));
    };

    const handleSiteChange = (_key: string, value: any, selectedSite?: any) => {
        if (otherSite) {
            let site = selectedSite || thirdPartySites?.find((option: AutocompleteOption) => option.id === value) || null;
            setFormData((prev) => ({
                ...prev,
                site_name: value,
                site_description: site?.description || null,
                address: site?.address || null,
                what3_words: site?.what3_words || null,
                lat: site?.latitude || null,
                long: site?.longitude || null,
                region: site?.network_id || prev.region
            }));

        } else {
            let site = selectedSite || getSiteOptions().find((option: AutocompleteOption) => option.id === value) || null;
            setFormData((prev) => ({
                ...prev,
                site_name: value,
                site_description: site?.description || null,
                address: site?.address || null,
                what3_words: site?.what3_words || null,
                lat: site?.lat || site?.latitude || null,
                long: site?.long || site?.longitude || null,
            }));
        }
        const error = validateField("site_name", value);
        setFieldErrors((prev) => ({ ...prev, site_name: error }));
    };

    const handleNetworkChange = (value: any) => {
        setFormData((prev) => ({
            ...prev,
            region: value,
            site_name: null,
            site_description: null,
            lat: null,
            long: null,
            address: null,
            what3_words: null,
        }));
        const error = validateField("region", value);
        setFieldErrors((prev) => ({ ...prev, region: error }));
    };

    const handleAddressChange = (addressData: { address: string; what3_words: string; lat: number; long: number }) => {
        setFormData((prev) => ({
            ...prev,
            address: addressData.address,
            what3_words: addressData.what3_words,
            lat: addressData.lat,
            long: addressData.long,
        }));
        const error = validateField("address", addressData.address);
        setFieldErrors((prev) => ({ ...prev, address: error }));
    };

    const handleAssetChange = (id: number, key: keyof Asset, value: any) => {
        setAssets((prev) =>
            prev.map((asset) => (asset.id === id ? { ...asset, [key]: value } : asset))
        );

        // Validate the changed field
        const error = validateAssetField(key as string, value);

        // If asset_description is changed, also check for duplicates across all assets
        if (key === "asset_description" && value && value.trim()) {
            const normalizedValue = value.trim().toLowerCase();
            const updatedAssets = assets.map((asset) =>
                asset.id === id ? { ...asset, [key]: value } : asset
            );

            // Check if this description already exists in other assets
            const isDuplicate = updatedAssets.some(
                (asset) =>
                    asset.id !== id &&
                    asset.asset_description &&
                    asset.asset_description.trim().toLowerCase() === normalizedValue
            );

            if (isDuplicate) {
                setAssetErrors((prev) => ({
                    ...prev,
                    [id]: {
                        ...(prev[id] || {}),
                        [key]: "This asset description is already used. Please use a unique description.",
                    },
                }));
                return;
            } else {
                // Clear duplicate errors from all assets with the same description if this one is being changed
                const clearedErrors = { ...assetErrors };
                Object.keys(clearedErrors).forEach((assetIdStr) => {
                    const assetId = parseInt(assetIdStr);
                    const asset = updatedAssets.find((a) => a.id === assetId);
                    if (
                        asset &&
                        asset.asset_description &&
                        asset.asset_description.trim().toLowerCase() === normalizedValue
                    ) {
                        if (clearedErrors[assetId]?.asset_description?.includes("already used")) {
                            delete clearedErrors[assetId].asset_description;
                            if (Object.keys(clearedErrors[assetId]).length === 0) {
                                delete clearedErrors[assetId];
                            }
                        }
                    }
                });
                setAssetErrors(clearedErrors);
            }
        }

        setAssetErrors((prev) => ({
            ...prev,
            [id]: {
                ...(prev[id] || {}),
                [key]: error,
            },
        }));
    };

    const handleAddAsset = () => {
        const newId = assets.length ? assets[assets.length - 1].id + 1 : 1;
        setAssets((prev) => [
            ...prev,
            {
                id: newId,
                asset_programme: null,
                narms_value: "",
                asset_category: null,
                asset_description: "",
                asset_type: null,
                iln_number_id: "",
                functional_location: "",
                sap_hierarchy: "",
                asset_budget: "",
            },
        ]);
    };


    const handleRemoveAsset = (id: number) => {
        setAssets((prev) => prev.filter((a) => a.id !== id));
    };

    const handleAddStakeholder = (stakeholder: { label: string; value: string }) => {
        setStakeholders((prev) => [...prev, stakeholder]);
    };

    const handleRemoveStakeholder = (id: string) => {
        setStakeholders((prev) => prev.filter((s) => s.value !== id));
    };

    const hasAnyError = (errors: Record<string, any>) => {
        return Object.values(errors).some(value => value !== null);
    };

    const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        setSubmitMessage(null);
        setIsSubmitting(true);

        const { isAllFieldValid } = validateAllFields();
        
        const isAssetsValid = validateAssets();

        if (!isAllFieldValid || (!isEdit && !isAssetsValid)) {
            setIsSubmitting(false);
            return;
        };

        const payload: any = {
            wbs_code_id: formData?.wbs_code_id ?? "",
            project_title: formData?.title ?? "",
            project_description: formData?.description ?? "",
            portfolio_id: formData?.portfolio,
            programme_id: formData?.programme,
            work_stream_id: Number(formData?.workstream),
            project_scope: formData?.project_scope ?? "",
            project_budget: Number(formData?.project_budget),
            project_income: formData?.project_income ? Number(formData.project_income) : null,
            site_id: formData?.site_name,
            org_id: formData?.org,
            region_id: Number(formData?.region),
            g_17: formData?.g_17 ?? "",
            address: formData?.address ?? "",
            latitude: Number(formData?.lat),
            longitude: Number(formData?.long),
            planned_start_date: formatApiStartDate(formData?.planned_start_date),
            planned_commissioning_date: formatApiEndDate(formData?.planned_commissioning_date),
            planned_close_up_date: formatApiEndDate(formData?.planned_close_up_date),
            project_year: formData?.project_year,
            work_type_id: formData?.work_type,
            work_group_id: formData?.work_type_group,
            asset_details: Array.isArray(assets) ? assets.map((asset) => ({
                programme_id: asset.asset_programme,
                asset_category: asset.asset_category,
                asset_type: asset.asset_type,
                narms_value:
                    asset.asset_programme && asset.asset_programme === 6
                        ? Number(asset.narms_value)
                        : null,
                functional_location: asset?.functional_location ?? "",
                sap_hierarchy: asset?.sap_hierarchy ?? "",
                asset_budget: Number(asset.asset_budget),
                description: asset?.asset_description ?? "",
                ...(isEdit ? { asset_id: asset.asset_id } : {}),
                ...(asset.iln_number_id ? { iln_id: asset.iln_number_id } : {}),
            })) : [],
            stake_holders_details: stakeholders.map((s) => parseInt(s.value)),
            what3_words: formData?.what3_words,
        };

        if (isEdit) {
            payload["project_status"] = formData?.project_status;
        };

        try {
            if (isEdit && projectId) {
                await editProject.mutateAsync({ projectId, payload });
            } else {
                await createProject.mutateAsync(payload);
            }
            removeCache();
            setSubmitMessage({
                type: 'success',
                message: 'Project created successfully!'
            });
            setFormData({
                title: null,
                description: null,
                portfolio: null,
                programme: null,
                workstream: null,
                project_year: null,
                project_scope: null,
                wbs_code_id: null,
                project_budget: null,
                project_income: null,
                site_name: null,
                site_description: null,
                org: 1,
                region: null,
                g_17: null,
                project_status: null,
                address: null,
                what3_words: null,
                planned_start_date: null,
                planned_commissioning_date: null,
                planned_close_up_date: null,
                lat: null,
                long: null,
                work_type: null,
                work_type_group: null,
            });
            setAssets([{
                id: 1,
                asset_programme: null,
                narms_value: "",
                asset_category: null,
                asset_description: "",
                iln_number_id: "",
                asset_type: null,
                functional_location: "",
                sap_hierarchy: "",
                asset_budget: ""
            }]);
            setStakeholders([]);
        } catch (error: any) {

            const errorCode = error?.response?.data?.data?.code;
            const errorMessage = error?.response?.data?.data?.message;

            if (errorCode === "PROJECT_TITLE_ALREADY_EXISTS") {

                setFieldErrors((prev) => ({
                    ...prev,
                    title: errorMessage || "A project with this title already exists. Please use a different project title.",
                }));
            } else {
                setSubmitMessage({
                    type: 'error',
                    message: error?.response?.data?.message || error?.message || (isEdit ? 'Failed to update project. Please try again.' : 'Failed to create project. Please try again.')
                });
            }
        } finally {
            setIsSubmitting(false);
        }

    };

    useEffect(() => {
        if (isEdit && projectData && !isProjectDetailsPending) {
            setFormData({
                title: projectData.title || null,
                description: projectData.description || null,
                portfolio: projectData.portfolio_id || null,
                programme: projectData.programme_id || null,
                project_year: projectData.project_year
                    ? String(projectData.project_year)
                    : null,
                workstream: Number(projectData.workstream_id) || null,
                project_scope: projectData.project_scope || null,
                wbs_code_id: projectData.wbs_code_id
                    ? String(projectData.wbs_code_id)
                    : null,
                project_budget: projectData.project_budget
                    ? String(projectData.project_budget)
                    : null,
                project_income: projectData.project_income || null,
                site_name: projectData.site_id || null,
                site_description: projectData.site_description || null,
                org: Number(projectData.org_id) || null,
                region: Number(projectData.region_id) || null,
                g_17: projectData.g_17 || null,
                project_status: projectData.project_status || null,
                address: projectData.address || null,
                what3_words: projectData.what3_words || null,
                planned_start_date: projectData.planned_start_date || null,
                planned_commissioning_date:
                    projectData.planned_commissioning_date || null,
                planned_close_up_date: projectData.planned_close_up_date || null,
                lat: projectData.latitude ? Number(projectData.latitude) : null,
                long: projectData.longitude
                    ? Number(projectData.longitude)
                    : null,
                work_type: projectData.work_type_id || null,
                work_type_group: projectData.work_group_id || null,
            });
        }
    }, [isEdit, projectData, isProjectDetailsPending]);

    useEffect(() => {
        const fetchSiteOptions = async () => {
            if (formData?.site_name) {
                try {
                    const raw = getOptions('site');
                    const options = Array.isArray(raw) ? raw : [];
                    const siteOption = options.find((option: AutocompleteOption) => option.id === formData.site_name);
                    if (siteOption) {
                        setFormData((prev) => ({
                            ...prev,
                            address: siteOption.address,
                            what3_words: siteOption.what3_words,
                            county: siteOption.county_id,
                            lat: siteOption.latitude ? Number(siteOption.latitude) : null,
                            long: siteOption.longitude ? Number(siteOption.longitude) : null,
                        }));
                    }
                } catch (error) {
                    console.error("Failed to fetch site options:", error);
                }
            }
        };
        fetchSiteOptions();
    }, [formData.site_name]);

    return {
        formData, fieldErrors, stakeholders, assets, assetErrors, openStakeholderDialog, isSubmitting, submitMessage, otherSite, thirdPartySites,
        getSiteOptions, handleOtherSiteChange, setOtherSite, setOpenStakeholderDialog, handleAddAsset, handleRemoveAsset, handleSubmit, setFormData, setSubmitMessage,
        handleFieldChange, handleNetworkChange, handleSiteChange, handleAddressChange, handleAssetChange, handleAddStakeholder, handleRemoveStakeholder, refetchThirdPartySites
    };

};